import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import DatabaseIcon from "../../../../../../../DesignEditor/components/Icons/DatabaseIcon";
import DuplicateIcon from "../../../../../../../DesignEditor/components/Icons/DuplicateIcon";
import DeleteIcon from "../../../../../../../assets/images/icons/DeleteIcon";
import BriaIconButton from "../../../../../../../components/common/BriaIconButton/BriaIconButton";
import ConfirmationPopup from "../../../../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import RouterConstants from "../../../../../../../constants/RouterConstants";
import useSecureNavigate from "../../../../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { TgDataset } from "../../../../../Datasets/store/tg-dataset-store.tsx";
import { ViewTgModelLocationState } from "../../../../../Models/Viewer/TgModelViewer";
import { TgModel } from "../../../../../Models/store/tg-model-store";
import { isLegacyTgRoute } from "../../../../../utils/tg-utils.ts";
import styles from "./TgModelActions.module.scss";

type TgModelActionsProps = {
	model: TgModel;
	dataset?: TgDataset;
};

const TgModelActions = ({ model, dataset }: TgModelActionsProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.dashboard.table.actions" });
	const navigate = useSecureNavigate();
	const { tgStore } = useAppStore();
	const { tgModelStore } = tgStore;
	const [openDeletePopup, setOpenDeletePopup] = useState(false);

	const duplicateModel = async () => {
		tgModelStore.modelForm = {
			...model,
			id: NaN,
			name: `${t("duplicatedModelNamePrefix")} ${model.name}`,
		};
		const base = isLegacyTgRoute()
			? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
			: RouterConstants.NEW_TG.PROJECTS.fullPath;
		navigate(`${base}/${model.project_id}/${RouterConstants.NEW_TG.MODELS.createPath}`);
	};

	const openModelDataset = () => {
		const isLegacyRoute = isLegacyTgRoute();
		if (dataset?.upload_type == "advanced" && isLegacyRoute) {
			navigate(
				`${RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath}/${dataset.project_id}/${RouterConstants.NEW_TG.DATASETS.path}/${dataset.id}/advanced`,
			);
		} else {
			const modelToViewState: ViewTgModelLocationState = {
				modelToView: model,
			};
			navigate(
				`${
					isLegacyRoute
						? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
						: RouterConstants.NEW_TG.PROJECTS.fullPath
				}/${model.project_id}/${RouterConstants.NEW_TG.MODELS.path}/${model.id}/${
					RouterConstants.NEW_TG.MODELS.DATASET.path
				}/${model.dataset_id}`,
				{ state: modelToViewState },
			);
		}
	};

	const deleteModel = async () => {
		try {
			await tgModelStore.deleteModel(model.id);
		} finally {
			setOpenDeletePopup(false);
		}
	};

	return (
		<>
			<Box className={styles.actions}>
				<BriaIconButton className={styles.iconButton} title={t("duplicate")} onClick={() => duplicateModel()}>
					<DuplicateIcon />
				</BriaIconButton>
				<BriaIconButton className={styles.iconButton} title={t("dataset")} onClick={() => openModelDataset()}>
					<DatabaseIcon />
				</BriaIconButton>
				<BriaIconButton
					className={styles.iconButton}
					title={t("delete")}
					disabled={model.status === "InProgress"}
					onClick={() => setOpenDeletePopup(true)}
				>
					<DeleteIcon />
				</BriaIconButton>
			</Box>
			<ConfirmationPopup
				open={openDeletePopup}
				onClick={deleteModel}
				loading={tgModelStore.loadingDeleteModel}
				title={t("deletePopup.title")}
				description={<Trans>{t("deletePopup.description")}</Trans>}
				confirmButtonText={t("deletePopup.confirmBtn")}
				onClose={() => setOpenDeletePopup(false)}
			/>
		</>
	);
};

export default observer(TgModelActions);
